import React, { useEffect, useState } from 'react'
import { Button, Flex, Input, Spin, message } from 'antd'
import { format } from 'date-fns'
import AuthSelectors from 'modules/domain/auth/selectors'
import { addNote, getNotesList, updateNote } from 'modules/domain/note/managers'
import type { Note } from 'modules/domain/note/types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { NoteType } from 'types/entities'
import type { SeedInventory } from '../SeedInventoryTable'
import STNoteDropdownActions from 'views/components/SeedTracker/STNoteDropdownActions'
import {
  seedInventoryNotesEditActionsStyle,
  seedInventoryNotesEmptyStyle,
  seedInventoryNotesItemStyle,
  seedInventoryNotesLoadingStyle,
  seedInventoryNotesMarkerStyle,
  seedInventoryNotesMetaStyle,
  seedInventoryNotesMoreActionsWrapStyle,
  seedInventoryNotesRailStyle,
  seedInventoryNotesRootStyle,
  seedInventoryNotesTextStyle,
  seedInventoryNotesTimelineStyle,
} from '../../styles'

type SeedInventoryNotesProps = {
  record?: SeedInventory | null
  isAddingNote?: boolean
  onCancelAddNote?: () => void
  onSaveAddNote?: () => void
  editMode?: boolean
  draftNoteText?: string
  onDraftNoteTextChange?: (value: string) => void
}

function formatNoteMeta(createdAt: string | undefined, createdBy: string | undefined): string {
  if (!createdAt && !createdBy) return ''
  if (!createdAt) return createdBy ?? ''
  const date = new Date(createdAt)
  const datePart = Number.isNaN(date.getTime()) ? createdAt : format(date, 'dd MMMM yyyy h:mm a')
  return createdBy ? `${datePart} - ${createdBy}` : datePart
}

const SeedInventoryNotes: React.FC<SeedInventoryNotesProps> = ({
  record,
  isAddingNote = false,
  onCancelAddNote = () => undefined,
  onSaveAddNote = () => undefined,
  editMode = false,
  draftNoteText,
  onDraftNoteTextChange,
}) => {
  const { t } = useTranslation(['notes'])
  const profile = useSelector(AuthSelectors.profile)
  const [noteText, setNoteText] = useState('')
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const currentUserFullName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
  const hideMoreOptions = editMode || isAddingNote || editingNoteId !== null

  const isCurrentUserNote = (createdBy?: string) =>
    !!currentUserFullName && (createdBy ?? '').trim().toLowerCase() === currentUserFullName.toLowerCase()

  useEffect(() => {
    if (!record?.id) {
      setNotes([])
      return
    }
    let cancelled = false
    setLoading(true)
    getNotesList({ parent_id: record.id, note_type: NoteType.SeedInventoryNote }, {}, 1, 500)
      .then((res) => {
        if (!cancelled) setNotes(res.data ?? [])
      })
      .catch(() => {
        if (!cancelled) void message.error(t('notes:errors.fetchFailed', 'Failed to load notes.'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [record?.id, t])

  const handleCancel = () => {
    setNoteText('')
    onCancelAddNote()
  }

  const handleSave = async () => {
    if (!record?.id || !noteText.trim()) {
      onSaveAddNote()
      return
    }
    const createdBy = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
    setSaving(true)
    try {
      const created = await addNote({
        parent_id: record.id,
        note_type: NoteType.SeedInventoryNote,
        note_text: noteText.trim(),
        created_by: createdBy || undefined,
      })
      setNotes((prev) => [created, ...prev])
      setNoteText('')
      onSaveAddNote()
    } catch {
      void message.error(t('notes:errors.saveFailed', 'Failed to save note.'))
    } finally {
      setSaving(false)
    }
  }

  const handleEditNote = (note: Note) => {
    setEditingNoteId(note.id)
    setEditText(note.note_text ?? '')
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditText('')
  }

  const handleSaveEdit = async () => {
    if (!editingNoteId || !editText.trim()) {
      handleCancelEdit()
      return
    }
    setEditSaving(true)
    try {
      const updated = await updateNote(editingNoteId, { note_text: editText.trim() })
      setNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)))
      setEditingNoteId(null)
      setEditText('')
      void message.success(t('notes:actions.updateSuccess', 'Note updated.'))
    } catch {
      void message.error(t('notes:actions.updateFailed', 'Failed to update note.'))
    } finally {
      setEditSaving(false)
    }
  }

  const handleDeleteNote = (noteId: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId))
  }

  const textAreaValue = editMode ? draftNoteText ?? '' : noteText

  return (
    <Flex vertical gap="small" style={seedInventoryNotesRootStyle}>
      {(editMode || (isAddingNote && !editingNoteId)) && (
        <>
          <Input.TextArea
            value={textAreaValue}
            onChange={(event) => {
              if (editMode) {
                onDraftNoteTextChange?.(event.target.value)
              } else {
                setNoteText(event.target.value)
              }
            }}
            placeholder="Add Note..."
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          {!editMode ? (
            <Flex justify="flex-end" gap={8}>
              <Button onClick={handleCancel} disabled={saving}>
                {t('notes:form.cancel', 'Cancel')}
              </Button>
              <Button type="primary" onClick={handleSave} loading={saving} disabled={!noteText.trim()}>
                {t('notes:form.save', 'Save')}
              </Button>
            </Flex>
          ) : null}
        </>
      )}
      {loading ? (
        <Flex justify="center" style={seedInventoryNotesLoadingStyle}>
          <Spin />
        </Flex>
      ) : notes.length === 0 ? (
        <div style={seedInventoryNotesEmptyStyle}>{t('notes:list.empty', 'No notes yet.')}</div>
      ) : (
        <div style={seedInventoryNotesTimelineStyle}>
          <div style={seedInventoryNotesRailStyle} />
          <Flex vertical gap={16}>
            {notes.map((note) => (
              <div key={note.id} style={seedInventoryNotesItemStyle}>
                {editingNoteId === note.id ? (
                  <>
                    <Input.TextArea
                      value={editText}
                      onChange={(event) => setEditText(event.target.value)}
                      placeholder="Edit Note..."
                      autoSize={{ minRows: 3, maxRows: 6 }}
                    />
                    <Flex justify="flex-end" gap={8} style={seedInventoryNotesEditActionsStyle}>
                      <Button onClick={handleCancelEdit} disabled={editSaving}>
                        {t('notes:form.cancel', 'Cancel')}
                      </Button>
                      <Button type="primary" onClick={handleSaveEdit} loading={editSaving} disabled={!editText.trim()}>
                        {t('notes:form.save', 'Save')}
                      </Button>
                    </Flex>
                  </>
                ) : (
                  <>
                    <div style={seedInventoryNotesMarkerStyle} />
                    {!hideMoreOptions && isCurrentUserNote(note.created_by) ? (
                      <div style={seedInventoryNotesMoreActionsWrapStyle}>
                        <STNoteDropdownActions note={note} onEdit={handleEditNote} onDeleteSuccess={handleDeleteNote} />
                      </div>
                    ) : null}
                    <div style={seedInventoryNotesTextStyle}>{note.note_text}</div>
                    <div style={seedInventoryNotesMetaStyle}>{formatNoteMeta(note.created_at, note.created_by)}</div>
                  </>
                )}
              </div>
            ))}
          </Flex>
        </div>
      )}
    </Flex>
  )
}

export default SeedInventoryNotes
